import { HttpClient, provideHttpClient, withInterceptors, withInterceptorsFromDi } from '@angular/common/http';
import { ApplicationConfig, ErrorHandler, importProvidersFrom } from '@angular/core';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter, withComponentInputBinding, withInMemoryScrolling, withViewTransitions } from '@angular/router';
import { provideTranslateService, TranslateLoader } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { authInterceptor, provideAuth, withAppInitializerAuthCheck } from 'angular-auth-oidc-client';
import {
    ArrowRight,
    BanknoteArrowDown,
    BanknoteArrowUp,
    Check,
    ChevronDown,
    ChevronUp,
    Circle,
    CircleAlert,
    CircleX,
    Clock,
    Cookie,
    CookingPot,
    Download,
    Eye,
    File,
    FileKey,
    FileLock,
    FileText,
    HandHeart,
    House,
    IdCard,
    Info,
    LayoutDashboard,
    List,
    LogOut,
    LucideAngularModule,
    Mail,
    MailCheck,
    MapPin,
    Megaphone,
    Menu,
    MessageCircle,
    MessageCircleMore,
    MessageSquare,
    MonitorSmartphone,
    PartyPopper,
    Pencil,
    Phone,
    PhoneCall,
    PhoneIncoming,
    Plus,
    RotateCcw,
    SearchX,
    ShieldCheck,
    ShieldMinus,
    Siren,
    SquareArrowOutUpRight,
    SquarePen,
    ThumbsUp,
    TimerReset,
    Trash,
    Trash2,
    User,
    Wrench,
    X,
} from 'lucide-angular';
import { MessageService } from 'primeng/api';
import { providePrimeNG } from 'primeng/config';
import { environment } from '../environments/environment';
import { Configuration } from './_generated';
import { ApiModule as OpenApiModule } from './_generated/api.module';
import { APP_ROUTES } from './app.routes';
import { GlobalErrorHandler } from './core/error-handlers/error-handler.service';
import { adminTokenInterceptor, tokenInterceptor } from './core/interceptors/add-token.interceptor';
import { AZURE_AD_CONFIG, AZURE_B2C_CONFIG, AZURE_B2C_PASSWORD_RESET_CONFIG } from './features/auth/auth.config';
import { PAX_THEME } from './pax-primeng.theme';
import { CurrencyPipe, DatePipe } from '@angular/common';
import { NgxEchartsModule } from 'ngx-echarts';

// Define the specific icons we want to use
const appIcons = {
    ArrowRight,
    BanknoteArrowDown,
    BanknoteArrowUp,
    Check,
    ChevronDown,
    ChevronUp,
    Circle,
    CircleAlert,
    CircleX,
    Clock,
    Cookie,
    CookingPot,
    Download,
    Eye,
    File,
    FileLock,
    FileText,
    FileKey,
    HandHeart,
    House,
    IdCard,
    Info,
    LayoutDashboard,
    List,
    LogOut,
    Mail,
    MapPin,
    Megaphone,
    Menu,
    MessageCircle,
    MessageCircleMore,
    MessageSquare,
    MonitorSmartphone,
    MailCheck,
    Pencil,
    PartyPopper,
    Phone,
    PhoneCall,
    PhoneIncoming,
    Plus,
    RotateCcw,
    SearchX,
    ShieldCheck,
    ShieldMinus,
    Siren,
    SquareArrowOutUpRight,
    SquarePen,
    ThumbsUp,
    TimerReset,
    Trash,
    Trash2,
    User,
    Wrench,
    X,
};

const httpLoaderFactory: (http: HttpClient) => TranslateHttpLoader = (http: HttpClient) => new TranslateHttpLoader(http, './i18n/', '.json');

export const appConfig: ApplicationConfig = {
    providers: [
        DatePipe,
        CurrencyPipe,
        provideRouter(
            APP_ROUTES,
            withComponentInputBinding(),
            withViewTransitions(),
            withInMemoryScrolling({
                scrollPositionRestoration: 'top',
            }),
        ),
        provideAnimations(),
        providePrimeNG({
            theme: PAX_THEME,
            translation: { dateFormat: 'dd.mm.yy' },
        }),
        provideTranslateService({
            defaultLanguage: 'de',
            loader: {
                provide: TranslateLoader,
                useFactory: httpLoaderFactory,
                deps: [HttpClient],
            },
        }),
        MessageService,
        { provide: ErrorHandler, useClass: GlobalErrorHandler },
        importProvidersFrom(LucideAngularModule.pick(appIcons)),
        importProvidersFrom(
            OpenApiModule.forRoot(
                () =>
                    new Configuration({
                        basePath: environment.apiUrl,
                    }),
            ),
        ),
        importProvidersFrom(
            NgxEchartsModule.forRoot({
                echarts: () => import('echarts'),
            }),
        ),
        provideAuth(
            {
                config: [AZURE_B2C_CONFIG, AZURE_B2C_PASSWORD_RESET_CONFIG, AZURE_AD_CONFIG],
            },
            withAppInitializerAuthCheck(),
        ),

        provideHttpClient(withInterceptorsFromDi(), withInterceptors([authInterceptor(), tokenInterceptor, adminTokenInterceptor])),
    ],
};
